"use client";

import { css } from "@emotion/react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { CheckIcon, ChevronDownIcon, InfoIcon, XIcon } from "lucide-react";
import * as React from "react";
import type { DateRange } from "react-day-picker";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useStorefrontSettingsState } from "@/hooks/useStorefrontSettingsState";
import { useTranslate } from "@/hooks/useTranslate/useTranslate";
import { cn } from "@/lib/utils";
import { Calendar2 } from "./calendar2";

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

export type TimeRange = {
	from: string | undefined;
	to?: string | undefined;
};

const parseTimeToMinutes = (timeValue?: string) => {
	if (!timeValue) {
		return undefined;
	}
	const [hoursRaw, minutesRaw] = timeValue.split(":");
	const hours = Number.parseInt(hoursRaw ?? "", 10);
	const minutes = Number.parseInt(minutesRaw ?? "", 10);
	if (
		Number.isNaN(hours) ||
		Number.isNaN(minutes) ||
		hours < 0 ||
		hours > 23 ||
		minutes < 0 ||
		minutes > 59
	) {
		return undefined;
	}
	return hours * 60 + minutes;
};

const formatMinutesToTime = (minutes: number) => {
	const normalizedMinutes = ((minutes % 1440) + 1440) % 1440;
	const hours = Math.floor(normalizedMinutes / 60)
		.toString()
		.padStart(2, "0");
	const mins = (normalizedMinutes % 60).toString().padStart(2, "0");
	return `${hours}:${mins}`;
};

export function DateTimeRangePicker({
	label,
	value,
	disabled,
	disablePast,
	onChange,
	infoText,
	fromLabel,
	toLabel,
	className,
	required,
}: {
	label: string;
	value?: {
		dateRange: DateRange | undefined;
		timeRange: TimeRange | undefined;
	};
	disabled: boolean;
	disablePast: boolean;
	onChange: (value: {
		dateRange: DateRange | undefined;
		timeRange: TimeRange | undefined;
	}) => void;
	infoText?: string;
	fromLabel?: string;
	toLabel?: string;
	className?: string;
	required?: boolean;
}) {
	let initialFromTime: string | undefined = "";
	let initialToTime: string | undefined = "";
	if (value?.timeRange?.from) {
		initialFromTime = value?.timeRange?.from || "";
	}
	if (value?.timeRange?.to) {
		initialToTime = value?.timeRange?.to || "";
	}
	const valueFromDate = value?.dateRange?.from;
	const valueToDate = value?.dateRange?.to;
	const valueTimeRange = value?.timeRange;

	const [selectedDayRange, setSelectedDayRange] = React.useState<
		DateRange | undefined
	>(value?.dateRange);
	const [fromTime, setFromTime] = React.useState<string | undefined>(
		initialFromTime,
	);
	const [toTime, setToTime] = React.useState<string | undefined>(initialToTime);
	const [open, setOpen] = React.useState(false);
	const [currentTime, setCurrentTime] = React.useState(() => dayjs());
	const [calendarMonth, setCalendarMonth] = React.useState<Date>(
		value?.dateRange?.from
			? dayjs(value.dateRange.from).startOf("month").toDate()
			: dayjs().startOf("month").toDate(),
	);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const openTimeoutRef = React.useRef<number | null>(null);
	const [showAvailabilityInfo, setShowAvailabilityInfo] = React.useState(false);
	const translate = useTranslate();
	const [dateRangeValidationMessage, setDateRangeValidationMessage] =
		React.useState<string>("");
	const storefrontSettingsState = useStorefrontSettingsState();
	const storefrontSettingsData = storefrontSettingsState.get().settings;

	const toDateAdjustmentDays =
		storefrontSettingsData?.ToDateAdjustmentDays || 0;
	const showReservationHourFields =
		storefrontSettingsData?.ShowReservationHourFields || false;
	const enableHoursOfOperation =
		storefrontSettingsData?.EnableHoursOfOperation || false;
	const openTime24 = storefrontSettingsData?.OpenTime || "";
	const closeTime24 = storefrontSettingsData?.CloseTime || "";
	const openTime12 = dayjs(openTime24, "HH:mm", true).isValid()
		? dayjs(openTime24, "HH:mm").format("hh:mm A")
		: openTime24;
	const closeTime12 = dayjs(closeTime24, "HH:mm", true).isValid()
		? dayjs(closeTime24, "HH:mm").format("hh:mm A")
		: closeTime24;
	const enableOpenDays = storefrontSettingsData?.EnableOpenDays || false;
	const openSu = storefrontSettingsData?.OpenSunday || false;
	const openMo = storefrontSettingsData?.OpenMonday || false;
	const openTu = storefrontSettingsData?.OpenTuesday || false;
	const openWe = storefrontSettingsData?.OpenWednesday || false;
	const openTh = storefrontSettingsData?.OpenThursday || false;
	const openFr = storefrontSettingsData?.OpenFriday || false;
	const openSa = storefrontSettingsData?.OpenSaturday || false;

	let unappliedFromDayOfWeek = "";
	let unappliedFromDate = "";
	let unappliedToDayOfWeek = "";
	let unappliedToDate = "";
	if (selectedDayRange) {
		if (selectedDayRange.from) {
			unappliedFromDayOfWeek = dayjs(selectedDayRange?.from).format("ddd");
			unappliedFromDate = dayjs(selectedDayRange?.from).format("ll");
		}
		if (selectedDayRange.to) {
			unappliedToDayOfWeek = dayjs(selectedDayRange?.to).format("ddd");
			unappliedToDate = dayjs(selectedDayRange?.to).format("ll");
		}
	}
	//let isUnappliedDateRangeValid = true;

	let originalFromDayOfWeek = "";
	let originalFromDate = "";
	let originalFromTime = "";
	let originalToDayOfWeek = "";
	let originalToDate = "";
	let originalToTime = "";
	if (value?.dateRange) {
		if (value?.dateRange.from) {
			originalFromDayOfWeek = dayjs(value?.dateRange?.from).format("ddd");
			originalFromDate = dayjs(value?.dateRange?.from).format("ll");
			originalFromTime = value?.timeRange?.from
				? dayjs(value?.timeRange?.from || "00:00", "HH:mm").format("hh:mm A")
				: "";
		}
		if (value?.dateRange.to) {
			originalToDayOfWeek = dayjs(value?.dateRange?.to).format("ddd");
			originalToDate = dayjs(value?.dateRange?.to).format("ll");
			originalToTime = value?.timeRange?.to
				? dayjs(value?.timeRange?.to || "23:59", "HH:mm").format("hh:mm A")
				: "";
		}
	}

	let durationDays = "";
	const originalFromDateDayJs = dayjs(originalFromDate);
	const originalToDateDayJs = dayjs(originalToDate);
	const isOriginalDateTimeRangeValid =
		originalFromDateDayJs.isValid() &&
		originalToDateDayJs.isValid() &&
		originalToDateDayJs.diff(originalFromDateDayJs, "day") >= 0;
	if (isOriginalDateTimeRangeValid) {
		durationDays =
			"(" +
			(originalToDateDayJs.diff(originalFromDateDayJs, "day") + 1).toString() +
			" " +
			translate("days") +
			")";
	}

	React.useEffect(() => {
		if (!showReservationHourFields) {
			setFromTime(undefined);
			setToTime(undefined);
		}
	}, [showReservationHourFields]);

	React.useEffect(() => {
		if (open) {
			return;
		}
		const today = currentTime.startOf("day");
		const now = currentTime;
		const normalizedToAdjustmentDays =
			Number.isFinite(toDateAdjustmentDays) && toDateAdjustmentDays >= 0
				? Math.floor(toDateAdjustmentDays)
				: 0;
		const openDayByWeekIndex = [
			openSu,
			openMo,
			openTu,
			openWe,
			openTh,
			openFr,
			openSa,
		];
		const openMinutes = parseTimeToMinutes(openTime24);
		const closeMinutes = parseTimeToMinutes(closeTime24);
		const nowMinutes = now.hour() * 60 + now.minute();

		const isOpenDay = (dayValue: dayjs.Dayjs) => {
			if (!enableOpenDays) {
				return true;
			}
			return openDayByWeekIndex[dayValue.day()] ?? false;
		};

		const hasReservableHoursLeft = (dayValue: dayjs.Dayjs) => {
			if (
				!showReservationHourFields ||
				!enableHoursOfOperation ||
				openMinutes === undefined ||
				closeMinutes === undefined
			) {
				return true;
			}
			if (!dayValue.isSame(today, "day")) {
				return true;
			}
			if (openMinutes <= closeMinutes) {
				return nowMinutes <= closeMinutes;
			}
			return true;
		};

		const isReservableDay = (dayValue: dayjs.Dayjs) =>
			isOpenDay(dayValue) && hasReservableHoursLeft(dayValue);

		const findNextReservableDay = (startingDay: dayjs.Dayjs) => {
			let candidate = startingDay.startOf("day");
			for (let i = 0; i < 366; i += 1) {
				if (isReservableDay(candidate)) {
					return candidate;
				}
				candidate = candidate.add(1, "day");
			}
			return undefined;
		};

		const rawFrom = valueFromDate
			? dayjs(valueFromDate).startOf("day")
			: undefined;
		const rawTo = valueToDate ? dayjs(valueToDate).startOf("day") : undefined;

		let nextFrom = rawFrom;
		let nextTo = rawTo;
		let changed = false;
		let fromDateAdjusted = false;

		if (disablePast && nextFrom?.isBefore(today, "day")) {
			nextFrom = today;
			changed = true;
			fromDateAdjusted = true;
		}

		if (nextFrom) {
			const adjustedFrom = findNextReservableDay(nextFrom);
			if (adjustedFrom && !adjustedFrom.isSame(nextFrom, "day")) {
				nextFrom = adjustedFrom;
				changed = true;
				fromDateAdjusted = true;
			}
		}

		if (nextFrom && fromDateAdjusted) {
			nextTo = nextFrom.add(normalizedToAdjustmentDays, "day");
			changed = true;
		} else if (nextFrom && nextTo) {
			const minTo = nextFrom.add(normalizedToAdjustmentDays, "day");
			if (nextTo.isBefore(minTo, "day")) {
				nextTo = minTo;
				changed = true;
			}
		} else if (nextTo && disablePast && nextTo.isBefore(today, "day")) {
			const minToFromToday = today.add(normalizedToAdjustmentDays, "day");
			nextTo = minToFromToday;
			changed = true;
		}

		if (nextTo) {
			const adjustedTo = findNextReservableDay(nextTo);
			if (adjustedTo && !adjustedTo.isSame(nextTo, "day")) {
				nextTo = adjustedTo;
				changed = true;
			}
		}

		if (!changed) {
			return;
		}

		const nextDateRange: DateRange = {
			from: nextFrom?.toDate(),
			to: nextTo?.toDate(),
		};
		setSelectedDayRange(nextDateRange);
		onChange({
			dateRange: nextDateRange,
			timeRange: valueTimeRange,
		});
	}, [
		open,
		currentTime,
		valueFromDate,
		valueToDate,
		valueTimeRange,
		onChange,
		toDateAdjustmentDays,
		disablePast,
		enableOpenDays,
		enableHoursOfOperation,
		showReservationHourFields,
		openTime24,
		closeTime24,
		openSu,
		openMo,
		openTu,
		openWe,
		openTh,
		openFr,
		openSa,
	]);

	React.useEffect(() => {
		const timerId = window.setInterval(() => {
			setCurrentTime(dayjs());
		}, 60_000);
		return () => window.clearInterval(timerId);
	}, []);

	React.useEffect(() => {
		return () => {
			if (openTimeoutRef.current !== null) {
				window.clearTimeout(openTimeoutRef.current);
			}
		};
	}, []);

	React.useEffect(() => {
		if (open || !showReservationHourFields || !valueTimeRange) {
			return;
		}

		const openMinutes = parseTimeToMinutes(openTime24);
		const closeMinutes = parseTimeToMinutes(closeTime24);
		const hoursOfOperationIsValid =
			enableHoursOfOperation &&
			openMinutes !== undefined &&
			closeMinutes !== undefined;
		const fallbackFromMinutes = hoursOfOperationIsValid ? openMinutes : 0;
		const fallbackToMinutes = hoursOfOperationIsValid
			? closeMinutes
			: 23 * 60 + 59;

		const isWithinOperatingHours = (timeInMinutes: number) => {
			if (
				!enableHoursOfOperation ||
				openMinutes === undefined ||
				closeMinutes === undefined
			) {
				return true;
			}
			if (openMinutes <= closeMinutes) {
				return timeInMinutes >= openMinutes && timeInMinutes <= closeMinutes;
			}
			return timeInMinutes >= openMinutes || timeInMinutes <= closeMinutes;
		};

		const hasFromTime = !!valueTimeRange.from;
		const hasToTime = !!valueTimeRange.to;
		let nextFromMinutes = parseTimeToMinutes(valueTimeRange.from);
		let nextToMinutes = parseTimeToMinutes(valueTimeRange.to);
		let changed = false;

		if (
			hasFromTime &&
			(nextFromMinutes === undefined ||
				!isWithinOperatingHours(nextFromMinutes))
		) {
			nextFromMinutes = fallbackFromMinutes;
			changed = true;
		}

		if (
			hasToTime &&
			(nextToMinutes === undefined || !isWithinOperatingHours(nextToMinutes))
		) {
			nextToMinutes = fallbackToMinutes;
			changed = true;
		}

		const isSameDayRange =
			!!valueFromDate &&
			!!valueToDate &&
			dayjs(valueFromDate).isSame(dayjs(valueToDate), "day");
		if (
			isSameDayRange &&
			hasFromTime &&
			hasToTime &&
			nextFromMinutes !== undefined &&
			nextToMinutes !== undefined &&
			nextToMinutes < nextFromMinutes
		) {
			nextToMinutes = fallbackToMinutes;
			changed = true;
			if (nextToMinutes < nextFromMinutes) {
				nextFromMinutes = fallbackFromMinutes;
			}
		}

		if (!changed) {
			return;
		}

		const nextTimeRange: TimeRange = {
			from:
				hasFromTime && nextFromMinutes !== undefined
					? formatMinutesToTime(nextFromMinutes)
					: valueTimeRange.from,
			to:
				hasToTime && nextToMinutes !== undefined
					? formatMinutesToTime(nextToMinutes)
					: valueTimeRange.to,
		};

		setFromTime(nextTimeRange.from);
		setToTime(nextTimeRange.to);
		onChange({
			dateRange: {
				from: valueFromDate,
				to: valueToDate,
			},
			timeRange: nextTimeRange,
		});
	}, [
		open,
		showReservationHourFields,
		valueTimeRange,
		valueFromDate,
		valueToDate,
		enableHoursOfOperation,
		openTime24,
		closeTime24,
		onChange,
	]);

	React.useEffect(() => {
		const setValidationMessage = (message: string) => {
			setDateRangeValidationMessage((prev) =>
				prev === message ? prev : message,
			);
		};
		const openDayByWeekIndex = [
			openSu,
			openMo,
			openTu,
			openWe,
			openTh,
			openFr,
			openSa,
		];
		const dayNameByWeekIndex = [
			translate("Sunday"),
			translate("Monday"),
			translate("Tuesday"),
			translate("Wednesday"),
			translate("Thursday"),
			translate("Friday"),
			translate("Saturday"),
		];
		const activeFromTime = showReservationHourFields ? fromTime : undefined;
		const activeToTime = showReservationHourFields ? toTime : undefined;

		let fromDayJs = dayjs(selectedDayRange?.from).startOf("day");
		let toDayJs = dayjs(selectedDayRange?.to).startOf("day");
		const fromTimeMinutes = parseTimeToMinutes(activeFromTime);
		const toTimeMinutes = parseTimeToMinutes(activeToTime);
		if (fromTimeMinutes !== undefined) {
			fromDayJs = fromDayJs
				.set("hour", Math.floor(fromTimeMinutes / 60))
				.set("minute", fromTimeMinutes % 60)
				.startOf("minute");
		}
		if (toTimeMinutes !== undefined) {
			toDayJs = toDayJs
				.set("hour", Math.floor(toTimeMinutes / 60))
				.set("minute", toTimeMinutes % 60)
				.startOf("minute");
		}
		const fromDateIsValid = fromDayJs.isValid();
		const toDateIsValid = toDayJs.isValid();
		const dateTimeRangeIsValid =
			fromDateIsValid &&
			toDateIsValid &&
			(fromDayJs.isBefore(toDayJs) || fromDayJs.isSame(toDayJs));
		// console.log({
		//     fromDayJs,
		//     toDayJs,
		//     fromDateIsValid,
		//     toDateIsValid,
		//     isBefore: fromDayJs.isBefore(toDayJs),
		//     isSame: fromDayJs.isSame(toDayJs)
		// })
		if (!dateTimeRangeIsValid) {
			//console.log("invalid");
			setValidationMessage("Invalid date range");
			return;
		} else {
			//console.log("valid");
		}

		if (enableOpenDays) {
			const fromDayOfWeek = fromDayJs.day();
			const isFromDayOfWeekValid = openDayByWeekIndex[fromDayOfWeek] ?? false;
			if (!isFromDayOfWeekValid) {
				setValidationMessage(
					translate("Invalid from date. The shop is closed on ") +
						(dayNameByWeekIndex[fromDayOfWeek] ?? ""),
				);
				return;
			}

			const toDayOfWeek = toDayJs.day();
			const isToDayOfWeekValid = openDayByWeekIndex[toDayOfWeek] ?? false;
			if (!isToDayOfWeekValid) {
				setValidationMessage(
					translate("Invalid to date. The shop is closed on ") +
						(dayNameByWeekIndex[toDayOfWeek] ?? ""),
				);
				return;
			}
		}

		if (
			showReservationHourFields &&
			enableHoursOfOperation &&
			(activeFromTime || activeToTime)
		) {
			const openMinutes = parseTimeToMinutes(openTime24);
			const closeMinutes = parseTimeToMinutes(closeTime24);
			if (openMinutes === undefined || closeMinutes === undefined) {
				setValidationMessage(
					translate("Unable to validate times against hours of operation"),
				);
				return;
			}

			const isWithinOperatingHours = (timeInMinutes: number) => {
				if (openMinutes <= closeMinutes) {
					return timeInMinutes >= openMinutes && timeInMinutes <= closeMinutes;
				}
				return timeInMinutes >= openMinutes || timeInMinutes <= closeMinutes;
			};

			if (activeFromTime) {
				if (fromTimeMinutes === undefined) {
					setValidationMessage(translate("Invalid start time"));
					return;
				}
				if (!isWithinOperatingHours(fromTimeMinutes)) {
					setValidationMessage(
						`${translate("Start time must be within hours of operation")}: ${openTime12} - ${closeTime12}`,
					);
					return;
				}
			}

			if (activeToTime) {
				if (toTimeMinutes === undefined) {
					setValidationMessage(translate("Invalid end time"));
					return;
				}
				if (!isWithinOperatingHours(toTimeMinutes)) {
					setValidationMessage(
						`${translate("End time must be within hours of operation")}: ${openTime12} - ${closeTime12}`,
					);
					return;
				}
			}
		}

		setValidationMessage("");
	}, [
		selectedDayRange,
		fromTime,
		toTime,
		openTime24,
		closeTime24,
		openTime12,
		closeTime12,
		openSu,
		openMo,
		openTu,
		openWe,
		openTh,
		openFr,
		openSa,
		enableOpenDays,
		enableHoursOfOperation,
		showReservationHourFields,
		translate,
	]);

	const prepareOpenState = () => {
		setSelectedDayRange(value?.dateRange);
		setCalendarMonth(
			value?.dateRange?.from
				? dayjs(value.dateRange.from).startOf("month").toDate()
				: dayjs().startOf("month").toDate(),
		);
		if (showReservationHourFields) {
			setFromTime(value?.timeRange?.from);
			setToTime(value?.timeRange?.to);
		} else {
			setFromTime(undefined);
			setToTime(undefined);
		}
	};

	const handleOpenChange = (nextOpen: boolean) => {
		if (openTimeoutRef.current !== null) {
			window.clearTimeout(openTimeoutRef.current);
			openTimeoutRef.current = null;
		}

		if (!nextOpen) {
			setOpen(false);
			return;
		}

		prepareOpenState();

		const isMobileViewport = window.matchMedia("(max-width: 640px)").matches;
		setShowAvailabilityInfo(!isMobileViewport);
		if (!isMobileViewport) {
			setOpen(true);
			return;
		}

		containerRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});
		openTimeoutRef.current = window.setTimeout(() => {
			setOpen(true);
			openTimeoutRef.current = null;
		}, 250);
	};

	const availabilityInfo = (enableHoursOfOperation || enableOpenDays) && (
		<div className="text-center">
			{enableHoursOfOperation && (
				<>
					<div className="text-xs font-bold">
						{translate("Hours of Operation")}:
					</div>
					<div className={enableOpenDays ? "pb-1" : undefined}>
						{openTime12} - {closeTime12}
					</div>
				</>
			)}
			{enableOpenDays && (
				<table className="mx-auto text-xs border bg-muted">
					<caption>{translate("Open Days")}</caption>
					<thead>
						<tr>
							<th className="text-center px-1">Su</th>
							<th className="text-center px-1">Mo</th>
							<th className="text-center px-1">Tu</th>
							<th className="text-center px-1">We</th>
							<th className="text-center px-1">Th</th>
							<th className="text-center px-1">Fr</th>
							<th className="text-center px-1">Sa</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>
								<div className="flex justify-center">
									{openSu ? (
										<CheckIcon size={12} className="text-green-700" />
									) : (
										<XIcon size={12} className="text-red-700" />
									)}
								</div>
							</td>
							<td>
								<div className="flex justify-center">
									{openMo ? (
										<CheckIcon size={12} className="text-green-700" />
									) : (
										<XIcon size={12} className="text-red-700" />
									)}
								</div>
							</td>
							<td>
								<div className="flex justify-center">
									{openTu ? (
										<CheckIcon size={12} className="text-green-700" />
									) : (
										<XIcon size={12} className="text-red-700" />
									)}
								</div>
							</td>
							<td>
								<div className="flex justify-center">
									{openWe ? (
										<CheckIcon size={12} className="text-green-700" />
									) : (
										<XIcon size={12} className="text-red-700" />
									)}
								</div>
							</td>
							<td>
								<div className="flex justify-center">
									{openTh ? (
										<CheckIcon size={12} className="text-green-700" />
									) : (
										<XIcon size={12} className="text-red-700" />
									)}
								</div>
							</td>
							<td>
								<div className="flex justify-center">
									{openFr ? (
										<CheckIcon size={12} className="text-green-700" />
									) : (
										<XIcon size={12} className="text-red-700" />
									)}
								</div>
							</td>
							<td>
								<div className="flex justify-center">
									{openSa ? (
										<CheckIcon size={12} className="text-green-700" />
									) : (
										<XIcon size={12} className="text-red-700" />
									)}
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			)}
		</div>
	);

	return (
		<div ref={containerRef} className={cn(["py-1", className])}>
			<div className="flex py-2 pb-1">
				<Label
					htmlFor="date-picker"
					className="flex-1 md:w-auto whitespace-nowrap flex items-center"
				>
					{label} {required && <span className="text-destructive">*</span>}
					{infoText && (
						<Tooltip>
							<TooltipTrigger>
								<InfoIcon size={16} />
							</TooltipTrigger>
							<TooltipContent>
								<p>{infoText}</p>
							</TooltipContent>
						</Tooltip>
					)}
				</Label>
				<div
					className="text-xs text-right"
					css={css`flex: 0 0 auto;padding:0 .5rem;`}
				>
					{durationDays}
				</div>
			</div>
			<div className="flex gap-1">
				<Popover open={open} onOpenChange={handleOpenChange}>
					<PopoverTrigger
						id="date-picker"
						className={cn(
							buttonVariants({ variant: "outline" }),
							"w-full h-10 justify-between font-normal",
						)}
						disabled={disabled}
					>
						<div className="text-xs text-left p-1 grid grid-cols-[auto_auto_1fr_auto]">
							<div>{fromLabel || translate("From")}:</div>
							<div className="pl-2">
								{originalFromDayOfWeek}
								{originalFromDayOfWeek ? "," : ""}
							</div>
							<div className="px-2">{originalFromDate}</div>
							<div>{showReservationHourFields ? originalFromTime : ""}</div>

							<div>{toLabel || translate("To")}:</div>
							<div className="pl-2">
								{originalToDayOfWeek}
								{originalToDayOfWeek ? "," : ""}
							</div>
							<div className="px-2">{originalToDate}</div>
							<div>{showReservationHourFields ? originalToTime : ""}</div>
						</div>
						<ChevronDownIcon />
						{/* <Calendar1Icon /> */}
					</PopoverTrigger>
					<PopoverContent
						className={cn([
							showReservationHourFields ? "max-w-85" : "max-w-65",
							"flex w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] max-h-[calc(100dvh-1rem)] overflow-y-auto overscroll-contain px-3 pb-4 sm:w-auto sm:max-h-[min(42rem,calc(100dvh-2rem))] sm:px-6",
						])}
						align="start"
					>
						{availabilityInfo && (
							<>
								<div className="hidden sm:block">{availabilityInfo}</div>
								<Collapsible
									open={showAvailabilityInfo}
									onOpenChange={setShowAvailabilityInfo}
									className="sm:hidden"
								>
									<CollapsibleTrigger
										className={cn(
											buttonVariants({ variant: "outline" }),
											"w-full justify-between text-xs",
										)}
									>
										<span>{translate("Store Availability Info")}</span>
										<ChevronDownIcon
											className={cn(
												"size-4 transition-transform",
												showAvailabilityInfo && "rotate-180",
											)}
										/>
									</CollapsibleTrigger>
									<CollapsibleContent className="pt-2">
										{availabilityInfo}
									</CollapsibleContent>
								</Collapsible>
							</>
						)}
						<div className="flex justify-center">
							<Calendar2
								disablePast={disablePast}
								mode="range"
								month={calendarMonth}
								selected={selectedDayRange}
								captionLayout="dropdown"
								onMonthChange={setCalendarMonth}
								onSelect={(selectedRange) => {
									if (selectedRange === undefined) {
										//setSelectedDateRange(undefined);
										setSelectedDayRange(undefined);
									} else {
										const dayRange = {
											from: selectedRange.from
												? dayjs(selectedRange.from).startOf("day").toDate()
												: undefined,
											to: selectedRange.to
												? dayjs(selectedRange.to).startOf("day").toDate()
												: undefined,
										};
										setSelectedDayRange(dayRange);
									}
									//setOpen(false)
								}}
							/>
						</div>

						<Button
							variant="outline"
							onClick={() => {
								setSelectedDayRange(undefined);
							}}
						>
							{translate("Reset Range")}
						</Button>

						<div>
							<div
								className={cn(
									"mx-auto text-sm text-left gap-2",
									showReservationHourFields
										? "grid grid-cols-[auto_auto_1fr_auto]"
										: "grid grid-cols-[auto_auto_1fr]",
								)}
							>
								<div className="flex items-center">{translate("From")}:</div>
								<div className="flex items-center">
									{unappliedFromDayOfWeek}
									{unappliedFromDayOfWeek ? "," : ""}
								</div>
								<div className="flex items-center">{unappliedFromDate}</div>
								{showReservationHourFields && (
									<div className="flex items-center">
										<Input
											type="time"
											id="from-time-picker"
											step={60}
											value={fromTime}
											className="w-28 bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
											disabled={disabled}
											onChange={(e) => {
												setFromTime(e.target.value);
											}}
										/>
									</div>
								)}

								<div className="flex items-center">{translate("To")}:</div>
								<div className="flex items-center">
									{unappliedToDayOfWeek}
									{unappliedToDayOfWeek ? "," : ""}
								</div>
								<div className="flex items-center">{unappliedToDate}</div>
								{showReservationHourFields && (
									<div className="flex items-center">
										<Input
											type="time"
											id="to-time-picker"
											step={60}
											value={toTime}
											className="w-28 bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
											disabled={disabled}
											onChange={(e) => {
												setToTime(e.target.value);
											}}
										/>
									</div>
								)}
							</div>
						</div>

						<Button
							disabled={!!dateRangeValidationMessage}
							onClick={() => {
								if (dateRangeValidationMessage) {
									return;
								}
								setOpen(false);
								const nextRange = {
									dateRange: {
										from: selectedDayRange?.from
											? dayjs(selectedDayRange.from).startOf("day").toDate()
											: undefined,
										to: selectedDayRange?.to
											? dayjs(selectedDayRange.to).startOf("day").toDate()
											: undefined,
									},
									timeRange: {
										from: showReservationHourFields ? fromTime : undefined,
										to: showReservationHourFields ? toTime : undefined,
									},
								};
								//setSelectedDateRange(nextRange);
								onChange(nextRange);
							}}
						>
							{translate("Select")}
						</Button>
						{!!dateRangeValidationMessage && (
							<Alert>
								<InfoIcon color="var(--color-red-400)" />
								<AlertTitle className="text-red-400"></AlertTitle>
								<AlertDescription className="max-w-55">
									{dateRangeValidationMessage}
								</AlertDescription>
							</Alert>
						)}
					</PopoverContent>
				</Popover>
			</div>
		</div>
	);
}
